import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Bookmark, BookmarkPlus, RefreshCw, Trash2, X } from "lucide-react";
import { toast } from "sonner";

export const DEFAULT_SORT = "ripeness:desc,last_evaluated_at:desc";
const SINGLE = ["tier", "funnel_stage", "therapeutic_area", "best_phase"];
const MULTI = ["study_role", "timing", "cro_status"];
const PAGING = ["sort", "skip", "limit", "include_excluded", "value_phase", "include_value"];

export const EMPTY_FILTERS = {
    tier: "", funnel_stage: "", therapeutic_area: "", best_phase: "",
    study_role: [], timing: [], cro_status: [],
    sort: DEFAULT_SORT, include_excluded: "", value_phase: "best", extra: {},
};

/** UI filter state → `POST /tracks/query` body (also used for `/facets/query`).
 *  `extra` carries constraints loaded from a saved view that the controls
 *  don't surface (exclude{}, q, ripeness_min, …) so the list stays faithful. */
export function filtersToBody(f, limit = 100) {
    const extra = f.extra || {};
    const include = { ...(extra.include || {}) };
    SINGLE.forEach((k) => { if (f[k]) include[k] = [String(f[k])]; });
    MULTI.forEach((k) => { if (f[k]?.length) include[k] = f[k]; else delete include[k]; });
    const body = { ...extra, include, sort: f.sort.split(",").filter(Boolean), include_excluded: f.include_excluded === "true", limit };
    if (f.value_phase && f.value_phase !== "best") body.value_phase = f.value_phase;
    delete body.skip;
    return body;
}

/** Saved-view body → UI filter state (single selects only take a lone value;
 *  multi-valued single fields stay in `extra.include`). */
export function bodyToFilters(body) {
    const b = JSON.parse(JSON.stringify(body || {}));
    const extra = {};
    Object.keys(b).forEach((k) => { if (!PAGING.includes(k)) extra[k] = b[k]; });
    extra.include = { ...(extra.include || {}) };
    const f = { ...EMPTY_FILTERS, extra };
    SINGLE.forEach((k) => {
        const v = extra.include[k];
        if (Array.isArray(v) && v.length === 1) { f[k] = String(v[0]); delete extra.include[k]; }
    });
    MULTI.forEach((k) => { f[k] = Array.isArray(extra.include[k]) ? extra.include[k] : []; delete extra.include[k]; });
    f.sort = (b.sort || []).join(",") || DEFAULT_SORT;
    f.include_excluded = b.include_excluded ? "true" : "";
    f.value_phase = b.value_phase || "best";
    return f;
}

/** Number of constraints carried in `extra` that the controls can't show. */
export function hiddenConstraintCount(extra) {
    if (!extra) return 0;
    const inc = Object.keys(extra.include || {}).length;
    const exc = Object.keys(extra.exclude || {}).length;
    const scalars = Object.keys(extra).filter((k) => !["include", "exclude"].includes(k) && extra[k] !== null && extra[k] !== undefined).length;
    return inc + exc + scalars;
}

/** Saved views (GET/POST/DELETE /v1/ripeness/views) — load one into the
 *  filters or save the current selection under a name. */
export function SavedViewsBar({ currentBody, activeView, onApply, onClear, hiddenCount }) {
    const [views, setViews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/v1/ripeness/views", { params: { limit: 200 } });
            setViews(r.data.items || []);
        } catch (e) {
            toast.error(`Saved views failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const apply = (id) => {
        const v = views.find((x) => x.view_id === id);
        if (v) onApply(v); else onClear();
    };

    const save = async () => {
        const name = window.prompt("Save the current filters as a view.\nName (unique within your scope):", activeView?.name ? `${activeView.name} (copy)` : "");
        if (name === null) return;
        if (!name.trim()) { toast.error("Name is required"); return; }
        setSaving(true);
        try {
            const { limit, ...body } = currentBody;
            const r = await api.post("/v1/ripeness/views", { name: name.trim(), description: "Saved from the QA Radar", body: { ...body, limit: 100 } });
            toast.success(`View "${r.data.name}" saved (${r.data.scope})`);
            setViews((prev) => [...prev, r.data].sort((a, b) => a.name.localeCompare(b.name)));
            onApply(r.data);
        } catch (e) {
            if (e?.response?.status === 409) toast.error("A view with this name already exists");
            else toast.error(`Save failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!activeView) return;
        if (!window.confirm(`Delete saved view "${activeView.name}"?`)) return;
        try {
            await api.delete(`/v1/ripeness/views/${encodeURIComponent(activeView.view_id)}`, { params: { reason: "Deleted from QA dashboard" } });
            toast.success("View deleted");
            setViews((prev) => prev.filter((v) => v.view_id !== activeView.view_id));
            onClear();
        } catch (e) {
            toast.error(`Delete failed: ${formatApiErrorDetail(e)}`);
        }
    };

    return (
        <div data-testid="saved-views-bar">
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Bookmark className="w-3 h-3" /> Saved view</span>
                {loading ? <RefreshCw className="w-3 h-3 animate-spin text-slate-500" /> : null}
            </div>
            <div className="flex items-center gap-1.5">
                <select
                    value={activeView?.view_id || ""}
                    onChange={(e) => apply(e.target.value)}
                    className="flex-1 min-w-0 rounded bg-slate-950/60 border border-slate-700 px-2 py-1.5 text-sm text-slate-100"
                    data-testid="saved-view-select"
                >
                    <option value="">— none —</option>
                    {views.map((v) => (
                        <option key={v.view_id} value={v.view_id}>{`${v.name}${v.scope === "global" ? " · global" : ""}`}</option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    title="Save current filters as a view"
                    className="rounded border border-slate-700 bg-slate-800/40 p-1.5 text-slate-200 hover:bg-slate-800 disabled:opacity-50"
                    data-testid="saved-view-save-btn"
                >
                    <BookmarkPlus className="w-4 h-4" />
                </button>
                {activeView ? (
                    <button
                        type="button"
                        onClick={remove}
                        title="Delete this view"
                        className="rounded border border-slate-700 bg-slate-800/40 p-1.5 text-rose-300 hover:bg-slate-800"
                        data-testid="saved-view-delete-btn"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                ) : null}
            </div>
            {activeView ? (
                <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="saved-view-active">
                    <span className="truncate">
                        <span className="text-slate-300">{activeView.name}</span>
                        {hiddenCount ? <span className="ml-1 text-amber-300" data-testid="saved-view-hidden-count">+{hiddenCount} constraint{hiddenCount > 1 ? "s" : ""} not shown</span> : null}
                    </span>
                    <button type="button" onClick={onClear} className="ml-auto text-slate-500 hover:text-slate-300" title="Clear view" data-testid="saved-view-clear-btn">
                        <X className="w-3 h-3" />
                    </button>
                </div>
            ) : null}
        </div>
    );
}
